import { Link } from "react-router-dom";
import type { CastMember } from "../lib/api.js";

/**
 * Principal cast — photo cards when TMDB headshots are available (rd: items),
 * otherwise a compact text row (library items, which store names only).
 * Shared by Watch (movies + episodes) and Series.
 *
 * Every name links to the person page (/person/<name>) — an IMDb-style
 * filmography that plays through the rd: pipeline.
 */
export function CastStrip({ cast, guestStars }: { cast: CastMember[]; guestStars?: CastMember[] }): JSX.Element | null {
  if (cast.length === 0 && (guestStars?.length ?? 0) === 0) return null;
  const hasPhotos = cast.some((c) => c.profile_url);
  const personHref = (name: string): string => `/person/${encodeURIComponent(name)}`;
  return (
    <div className="mt-4 space-y-3">
      {cast.length > 0 &&
        (hasPhotos ? (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {cast.map((c) => (
              <Link
                key={c.name}
                to={personHref(c.name)}
                className="w-20 shrink-0 group"
                title={`${c.name} — all movies & shows`}
              >
                {c.profile_url ? (
                  <img
                    src={c.profile_url}
                    alt={c.name}
                    loading="lazy"
                    className="w-20 h-28 object-cover rounded-md border border-hairline group-hover:border-gold/60 transition"
                  />
                ) : (
                  <div className="w-20 h-28 rounded-md border border-hairline bg-ink/60 group-hover:border-gold/60 transition" />
                )}
                <div className="text-[11px] text-ivory mt-1 leading-tight group-hover:text-gold transition">{c.name}</div>
                {c.character && (
                  <div className="text-[10px] text-parchment/60 leading-tight">as {c.character}</div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-parchment/70 text-sm">
            <span className="section-label mr-2">Cast</span>
            {cast.map((c, i) => (
              <span key={c.name}>
                {i > 0 && ", "}
                <Link to={personHref(c.name)} className="hover:text-gold transition underline-offset-2 hover:underline">
                  {c.name}
                </Link>
              </span>
            ))}
          </p>
        ))}
      {guestStars && guestStars.length > 0 && (
        <p className="text-parchment/70 text-sm">
          <span className="section-label mr-2">Guest stars</span>
          {guestStars.map((c, i) => (
            <span key={c.name}>
              {i > 0 && ", "}
              <Link to={personHref(c.name)} className="hover:text-gold transition underline-offset-2 hover:underline">
                {c.name}
              </Link>
              {c.character ? ` (${c.character})` : ""}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
